import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Trophy } from "lucide-react";
import { formatLapTime } from "./format.js";
import { useBranding } from "./BrandingContext.js";
function getDefaultRowStyle(position) {
    switch (position) {
        case 1:
            return {
                row: "bg-amber-500/90 text-gray-900",
                company: "text-gray-700",
                car: "text-gray-700",
                sector: "text-gray-700",
                sectorFastest: "text-fuchsia-700 font-semibold",
                isLight: true,
            };
        case 2:
            return {
                row: "bg-sky-500/80 text-white",
                company: "text-sky-100",
                car: "text-sky-100",
                sector: "text-sky-100",
                sectorFastest: "text-fuchsia-300 font-semibold",
                isLight: true,
            };
        case 3:
            return {
                row: "bg-indigo-500/80 text-white",
                company: "text-indigo-100",
                car: "text-indigo-100",
                sector: "text-indigo-100",
                sectorFastest: "text-fuchsia-300 font-semibold",
                isLight: true,
            };
        default:
            return {
                row: "bg-gray-800/80 text-gray-100 border border-gray-700/60",
                company: "text-gray-400",
                car: "text-gray-400",
                sector: "text-gray-400",
                sectorFastest: "text-fuchsia-400 font-semibold",
                isLight: false,
            };
    }
}
function getBrandingColors(position, branding) {
    switch (position) {
        case 1: return { bgColor: branding.pos1BgColor, textColor: branding.pos1TextColor };
        case 2: return { bgColor: branding.pos2BgColor, textColor: branding.pos2TextColor };
        case 3: return { bgColor: branding.pos3BgColor, textColor: branding.pos3TextColor };
        default: return { bgColor: branding.defaultBgColor, textColor: branding.defaultTextColor };
    }
}
function hexToRgb(hex) {
    const c = hex.replace("#", "");
    if (c.length !== 6)
        return null;
    return {
        r: parseInt(c.substring(0, 2), 16),
        g: parseInt(c.substring(2, 4), 16),
        b: parseInt(c.substring(4, 6), 16),
    };
}
function isLightColor(hex) {
    if (!hex)
        return false;
    const rgb = hexToRgb(hex);
    if (!rgb)
        return false;
    return (0.299 * rgb.r + 0.587 * rgb.g + 0.114 * rgb.b) / 255 > 0.5;
}
function SectorTime({ value, fastest, normalClass, fastestClass, normalStyle, }) {
    if (!value)
        return _jsx("span", { className: "text-gray-600", children: "\u2014" });
    const isFastest = fastest != null && value === fastest;
    return (_jsx("span", { className: isFastest ? fastestClass : normalClass, style: isFastest ? undefined : normalStyle, children: formatLapTime(value) }));
}
export function LeaderboardRow({ position, driverName, companyName, car, lapTime, sectors, fastestSectors, showSectors, extraColumns, pulse, }) {
    const branding = useBranding();
    const hasBrandingColors = branding && (branding.pos1BgColor || branding.defaultBgColor);
    const defaultStyle = getDefaultRowStyle(position);
    let rowClassName;
    let rowStyle = {};
    let subTextStyle = {};
    let companyClass = defaultStyle.company;
    let carClass = defaultStyle.car;
    let sectorClass = defaultStyle.sector;
    let isLight = defaultStyle.isLight;
    if (hasBrandingColors) {
        const { bgColor, textColor } = getBrandingColors(position, branding);
        isLight = isLightColor(bgColor);
        rowClassName = "rounded-lg";
        rowStyle = { backgroundColor: bgColor || undefined, color: textColor || undefined };
        subTextStyle = { opacity: 0.7 };
        companyClass = "";
        carClass = "";
        sectorClass = "";
    }
    else {
        rowClassName = `rounded-lg ${defaultStyle.row}`;
    }
    const pulseClass = pulse
        ? isLight ? "animate-leaderboard-pulse-light" : "animate-leaderboard-pulse"
        : "";
    return (_jsxs("div", { className: `lb-row flex items-center gap-4 px-5 py-3 bigscreen:gap-6 bigscreen:px-8 bigscreen:py-4 ${rowClassName} ${pulseClass}`, style: rowStyle, "data-position": position, children: [_jsx("div", { className: "lb-row-position flex-shrink-0 w-10 h-10 rounded-lg flex items-center justify-center font-bold text-xl bigscreen:w-16 bigscreen:h-16 bigscreen:rounded-xl bigscreen:text-[30px]", children: position === 1 ? (
                // SVG icon, not an emoji \u2014 Pi OS Lite ships no color emoji font, so
                // the previous "\uD83C\uDFC6" glyph rendered as a missing-glyph box on the
                // DisplayAgent. lucide draws an SVG path that follows currentColor,
                // so it themes correctly under both the default P1 amber row and
                // any branding-template text-colour override.
                _jsx(Trophy, { "aria-label": "1st place", className: "w-6 h-6 bigscreen:w-9 bigscreen:h-9", strokeWidth: 2 })) : (position) }), _jsxs("div", { className: "flex-1 min-w-0", children: [_jsx("div", { className: "lb-row-driver text-lg font-bold truncate bigscreen:text-[30px] bigscreen:leading-[36px]", children: driverName }), companyName && (_jsx("div", { className: `lb-row-company text-sm truncate bigscreen:text-[17px] bigscreen:leading-6 ${companyClass}`, style: subTextStyle, children: companyName }))] }), showSectors && sectors && (_jsxs("div", { className: "lb-row-sectors flex-shrink-0 flex gap-4 text-sm font-mono bigscreen:gap-6 bigscreen:text-[17px] bigscreen:leading-6", children: [_jsx(SectorTime, { value: sectors.s1, fastest: fastestSectors?.sector1Ms ?? null, normalClass: sectorClass, fastestClass: defaultStyle.sectorFastest, normalStyle: subTextStyle }), _jsx(SectorTime, { value: sectors.s2, fastest: fastestSectors?.sector2Ms ?? null, normalClass: sectorClass, fastestClass: defaultStyle.sectorFastest, normalStyle: subTextStyle }), _jsx(SectorTime, { value: sectors.s3, fastest: fastestSectors?.sector3Ms ?? null, normalClass: sectorClass, fastestClass: defaultStyle.sectorFastest, normalStyle: subTextStyle })] })), _jsxs("div", { className: "flex-shrink-0 text-right w-[180px] bigscreen:w-[240px]", children: [_jsx("div", { className: "lb-row-laptime font-mono text-lg font-bold bigscreen:text-[28px] bigscreen:leading-[34px]", children: lapTime }), car && (_jsx("div", { className: `lb-row-car text-sm truncate bigscreen:text-[17px] bigscreen:leading-6 ${carClass}`, style: subTextStyle, children: car }))] }), extraColumns?.map((col) => (_jsx("div", { className: "flex-shrink-0 text-sm w-16 text-right bigscreen:text-[17px] bigscreen:leading-6 bigscreen:w-24", children: col.value }, col.label)))] }));
}
//# sourceMappingURL=LeaderboardRow.js.map